import { http } from './http';
import { API_BASE_URL, ROUTES } from './config';
import { tokenStore } from './tokenStore';
import type { AccountingSummary, Invoice } from '../types/domain';
import type { CreatePaymentIntentInput, CreatePaymentIntentResult, RefundOrderPaymentInput } from '../types/ecommerce';

/** BillingService client (invoices, cash payments, refunds, accounting, Stripe order payments). manager+ unless noted. */
export const billingApi = {
  getInvoices(customerId?: string): Promise<Invoice[]> {
    const q = customerId ? `?customerId=${customerId}` : '';
    return http.get(`${ROUTES.invoices}${q}`);
  },

  recordCashPayment(invoiceId: string): Promise<void> {
    return http.post(`${ROUTES.payments}/cash`, { invoiceId });
  },

  /** Human-only refund — the AI can only *propose* refunds (backend enforces this). */
  refund(paymentId: string, reason: string): Promise<void> {
    return http.post(`${ROUTES.payments}/${paymentId}/refund`, { reason });
  },

  /** customer,manager — creates/reuses a Pending Payment row keyed by orderId, returns the
   * Stripe PaymentIntent's clientSecret for the frontend's Stripe Elements form. */
  createPaymentIntent(body: CreatePaymentIntentInput): Promise<CreatePaymentIntentResult> {
    return http.post(ROUTES.paymentIntent, body);
  },

  /** manager+ — order-scoped refund (as opposed to refund() above, which is paymentId-scoped);
   * omitting amount does a full refund. */
  refundOrderPayment(body: RefundOrderPaymentInput): Promise<void> {
    return http.post(ROUTES.paymentRefund, body);
  },

  getAccountingSummary(from: string, to: string): Promise<AccountingSummary> {
    return http.get(`${ROUTES.accounting}/summary?from=${from}&to=${to}`);
  },

  /** CSV export — bypasses the JSON client so we get the raw Blob with auth. */
  async exportCsv(from: string, to: string): Promise<Blob> {
    const token = tokenStore.get()?.accessToken;
    const response = await fetch(`${API_BASE_URL}${ROUTES.accounting}/export.csv?from=${from}&to=${to}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error(`Export failed (${response.status})`);
    return response.blob();
  },
};
